/**
 * Notes across a share on the writer's device (decision 76; part 38e log, DN1–DN12, tables RK, WN and PN), run in part
 * 38b's reconcile once the relay's list is read: each group's role key made, rotated and sent to its readers under their
 * person keys; the writer's shared notes sealed and pushed (or deleted) by the owner proof; the share's notes pulled,
 * opened, stored as note records and acknowledged once stored. Only-me notes are never read here (DN1). The book's turn
 * is held only around a record's read and write. Every effect is injected.
 */
import { ChapterPlaces, encodeShareNote, readShareNote, ScanBudget, toHex } from '@cockatrice/core';
import type { ShareNote, TextRange } from '@cockatrice/core';
import { openNote, sealNote, wrapRoleKey, writerTag, writerTagHolds, WRITER_TAG_BYTES } from '@cockatrice/crypto';
import type { NotePlace } from '@cockatrice/crypto';
import type { ApplyPulledResult, KeyGroup, NoteChangeResult, NoteRecord, NoteShare, PulledChange, ShareEnd, ShareRecord } from '@cockatrice/storage';
import type { RelayClient, RelayError } from '../account/client.ts';
import { toBase64Url } from '../account/bytes.ts';
import { earlierEnd, inviteOpen, toBase64Id } from './copies.ts';
import type { CopyBook } from './copies.ts';
import type { ShareSecrets } from './invite-secrets.ts';
import { MAX_NOTE_ACKS, MAX_NOTE_BYTES, readNoteSeq, readNotesAcknowledged, readOk, readPulledNotes, roleKeysBody, shareBody, writerNoteBody } from './invite-wire.ts';
import type { OwnerList, PulledNote, WireNoteGroup } from './invite-wire.ts';
import { itemOfNote, keysOf, noteOfPulled, planRoleKeys, withSent } from './note-plan.ts';
import type { LocatedNote } from './note-plan.ts';
import { bytesOfHex } from './secrets.ts';
import type { Random } from './secrets.ts';

/** What the notes keep in the local store, through the workspace (its lock, its history). */
export interface ShareNoteStore {
  /** The record of share `id`, or undefined. */
  share(id: string): Promise<ShareRecord | undefined>;
  /** Writes `record` again, under the lock; false when it wasn't written. */
  put(record: ShareRecord): Promise<boolean>;
  /** Book `bookId`'s notes, tombstones included (the readable ones). */
  notes(bookId: string): Promise<readonly NoteRecord[]>;
  /** The note under `id`, or undefined. */
  note(id: string): Promise<NoteRecord | undefined>;
  /** Table WN: what the relay holds of a note (part 38e's `Notes.markShared`; a range null drops it). */
  markShared(id: string, share: string, mark: Pick<NoteShare, 'epoch' | 'sent'> & { readonly range?: NoteShare['range'] | null }): Promise<NoteChangeResult>;
  /** Table PN: a pull's changes (part 38e's `Notes.applyPulled`). */
  applyPulled(changes: readonly PulledChange[]): Promise<ApplyPulledResult>;
  /** The notes read again for the views, once a run stored any. */
  notesStored(): Promise<void>;
}

/** What the notes run on. */
export interface ShareNoteDeps {
  /** The relay without a session (the owner's routes), or null with accounts off. */
  readonly relay: () => RelayClient | null;
  readonly store: ShareNoteStore;
  readonly subtle: SubtleCrypto;
  readonly random: Random;
  readonly now: () => number;
  /** Table DA: this device's public key (DA1); null for none, and then nothing is pulled. */
  readonly device: () => Promise<Uint8Array<ArrayBuffer> | null>;
  /** Runs `body` in book `bookId`'s turn (part 38b's RC8). */
  readonly turn: <T>(bookId: string, body: () => Promise<T>) => Promise<T>;
  /** DN12, RK12: the record ended at the relay's word. */
  readonly end: (record: ShareRecord, why: ShareEnd) => Promise<void>;
  /** RK11: the relay's list of the record read again. */
  readonly refresh: (record: ShareRecord, secrets: ShareSecrets) => Promise<unknown>;
}

/** Why a notes run stopped short, said on the Sharing page (WN9, WN11). */
export type NoteProblem = 'notes-full' | 'note-too-large' | 'failed';

/** What a notes run did. */
export interface NotesRun {
  readonly pushed: number;
  readonly stored: number;
  readonly problem: NoteProblem | null;
}

/** A note across this share, with its share part and its group. */
interface SharedNote {
  readonly note: NoteRecord;
  readonly shared: NoteShare;
  readonly group: WireNoteGroup;
}

/** AES-GCM's IV. */
const IV_BYTES = 12;

/** The refusals that mean the share is over at the relay (DN12). */
function endOf(error: RelayError): ShareEnd | null {
  if (error === 'share-ended') return 'ended';
  return error === 'share-gone' ? 'gone' : null;
}

/** SHA-256 of `bytes`, as hex. */
async function hashOf(subtle: SubtleCrypto, bytes: Uint8Array<ArrayBuffer>): Promise<string> {
  return toHex(new Uint8Array(await subtle.digest('SHA-256', bytes)));
}

/** Whether `sent` is a hash: the relay holds a version of the note (not `deleted`, `refused`, or nothing). */
function heldAtRelay(sent: NoteShare['sent']): boolean {
  return sent !== null && sent !== 'deleted' && sent !== 'refused';
}

/** The notes across a share. */
export class ShareNotes {
  readonly #deps: ShareNoteDeps;
  /**
   * Deep-fix r3, table AR: each chapter's text as last parsed for finding notes, so an unchanged chapter isn't parsed
   * again; deep-fix r6: with where each passage was found in it, by the anchor, so an unchanged chapter's notes aren't
   * found again either.
   */
  readonly #places = new Map<string, { readonly text: string; readonly places: ChapterPlaces; readonly found: Map<string, TextRange | null> }>();
  /** Deep-fix r6: each book's chapters by ID, while that book is the one read. */
  readonly #chapters = new WeakMap<CopyBook, ReadonlyMap<string, CopyBook['chapters'][number]>>();
  /** Deep-fix r6: each held note's `sent` and what decides its item, as they last matched, so an unchanged note isn't built or hashed again. */
  readonly #matched = new Map<string, string>();

  constructor(deps: ShareNoteDeps) {
    this.#deps = deps;
  }

  /**
   * DN10: share `id` of book `bookId`'s notes brought to the relay, by its list `list` read this run: the role keys (table
   * RK), the writer's notes (table WN), then the pull (table PN).
   */
  async run(bookId: string, id: string, secrets: ShareSecrets, list: OwnerList, book: CopyBook): Promise<NotesRun> {
    const relay = this.#deps.relay();
    if (relay === null) return { pushed: 0, stored: 0, problem: null };
    // NR8: a note across this share is never Only me.
    const notes = (await this.#deps.store.notes(bookId)).flatMap((note) => (note.shared?.share === id && note.visibility !== 'only-me' ? [{ note, shared: note.shared, group: note.visibility }] : []));
    const keyed = await this.#keys(bookId, id, secrets, list, notes, relay);
    const pushed = keyed === 'done' ? await this.#push(bookId, id, secrets, list, notes, relay, book) : { pushed: 0, problem: keyed };
    // Deep-fix r2, table EP: the pull runs whatever the keys and pushes met, an end too (DN12: notes wait to be pulled).
    const stored = await this.pull(bookId, id, secrets, book);
    return { pushed: pushed.pushed, stored, problem: pushed.problem === 'ended' ? null : pushed.problem };
  }

  /**
   * Table MP: the standing shared first notes of share `id` of book `bookId` on a passage, where it is found now in
   * `book`'s text, for its readers' manifests (MP1); one whose passage is gone with no range (MP4: the whole chapter).
   */
  async located(bookId: string, id: string, book: CopyBook): Promise<readonly LocatedNote[]> {
    const located: LocatedNote[] = [];
    for (const note of await this.#deps.store.notes(bookId)) {
      if (note.shared?.share !== id || note.deletedAt !== null || note.visibility === 'only-me' || note.anchor === null || note.parentId !== null) continue;
      located.push({ note, shared: note.shared, range: this.#located(note, book) });
    }
    return located;
  }

  /**
   * Deep-fix r2, table EP: share `id`'s notes pulled alone, as an ended share's are (its invitees' last notes wait at the
   * relay until it holds the share no more); how many were stored.
   */
  async pull(bookId: string, id: string, secrets: ShareSecrets, book: CopyBook): Promise<number> {
    const relay = this.#deps.relay();
    if (relay === null) return 0;
    const stored = await this.#pull(bookId, id, secrets, book, relay);
    if (stored > 0) await this.#deps.store.notesStored();
    return stored;
  }

  /** Table RK: the record's keys planned (new epochs stored first, DN4), then each send, its holders marked as the relay takes it. */
  async #keys(bookId: string, id: string, secrets: ShareSecrets, list: OwnerList, notes: readonly SharedNote[], relay: RelayClient): Promise<'done' | 'failed' | 'ended'> {
    const { subtle, random, now, store, turn } = this.#deps;
    // RK1: a group a writer's note waits for (standing, not yet sent) needs a key though no one reads it yet.
    const needed = new Set<KeyGroup>();
    for (const { note, shared, group } of notes) if (note.origin === 'writer' && note.deletedAt === null && shared.sent === null && group !== 'person') needed.add(group);
    const planned = await turn(bookId, async () => {
      const record = await store.share(id);
      if (record?.ended !== null) return null;
      const plan = planRoleKeys(record, list, now(), needed, () => toHex(random(32)));
      const made = plan.roleKeys.length !== record.roleKeys.length || plan.roleKeys.some((key, at) => key !== record.roleKeys[at]);
      // DN4: the new keys in the record before any request; unwritten, nothing goes.
      if (made && !(await store.put({ ...record, roleKeys: plan.roleKeys }))) return null;
      return plan;
    });
    if (planned === null) return 'ended';
    for (const send of planned.sends) {
      const to = send.to.map((invite) => invite.id);
      // Deep-fix r4, table KP: record first: pending before the post, so a lost answer still counts them if they leave.
      const marked = await turn(bookId, async () => {
        const record = await store.share(id);
        return record !== undefined && (await store.put(withSent(record, send.group, send.epoch, to, 'sending')));
      });
      if (!marked) return 'failed';
      const wraps = new Map<string, Uint8Array>();
      for (const invite of send.to) {
        const place = { share: secrets.id, invite: bytesOfHex(invite.id), group: send.group, epoch: send.epoch };
        wraps.set(toBase64Id(invite.id), await wrapRoleKey(subtle, bytesOfHex(invite.key), place, bytesOfHex(send.key), random(IV_BYTES)));
      }
      const sent = await relay.post('/v1/shares/role-keys', roleKeysBody(secrets.share, secrets.proof, send.group, send.epoch, wraps), readOk);
      // KP: no answer leaves them pending (sent again next run).
      if (sent.kind === 'failed') return 'failed';
      if (sent.kind === 'refused') {
        const end = endOf(sent.error);
        // RK11: an invite closed or changed meanwhile: the list read again, the rest at the next run; KP: a definite no
        // takes them out of pending.
        return turn(bookId, async () => {
          const record = await store.share(id);
          if (record === undefined) return 'ended';
          if (end !== null) await this.#deps.end(record, end);
          else {
            await store.put(withSent(record, send.group, send.epoch, to, 'refused'));
            await this.#deps.refresh(record, secrets);
          }
          return end === null ? 'failed' : 'ended';
        });
      }
      // RK17: the holders marked on the record as it stands now.
      await turn(bookId, async () => {
        const record = await store.share(id);
        if (record !== undefined) await store.put(withSent(record, send.group, send.epoch, to, 'taken'));
      });
    }
    return 'done';
  }

  /** Table WN: each of the writer's shared notes the relay doesn't hold as it stands, sent; each deleted here, deleted there. */
  async #push(bookId: string, id: string, secrets: ShareSecrets, list: OwnerList, notes: readonly SharedNote[], relay: RelayClient, book: CopyBook): Promise<{ readonly pushed: number; readonly problem: NoteProblem | 'ended' | null }> {
    const { store } = this.#deps;
    const byId = new Map(notes.map(({ note }) => [note.id, note]));
    let pushed = 0;
    let problem: NoteProblem | null = null;
    // Deep-fix r1, table RO: first notes before replies, and a reply only once its thread's first note is at the relay (sent
    // before, or this run), so no reply names a note the relay doesn't hold.
    const sentNow = new Set<string>();
    const ordered = [...notes].sort((a, b) => Number(a.note.parentId !== null) - Number(b.note.parentId !== null));
    for (const { note, shared, group } of ordered) {
      // WN4, WN5: a note deleted here (the writer's, or another's the writer deleted) that the relay may hold. Deep-fix r3
      // (table RF): whatever `sent` says (a post whose answer was lost, or one refused, may be there), but `deleted`, or a
      // group note whose post was never tried (no epoch written); a person's note's delete always goes (its epoch is 0).
      if (note.deletedAt !== null) {
        if (shared.sent === 'deleted' || (shared.sent === null && shared.epoch === 0 && group !== 'person')) continue;
        const gone = await relay.post('/v1/shares/note/delete', shareBody(secrets.share, secrets.proof, { id: toBase64Url(bytesOfHex(shared.note)) }), readOk);
        const outcome = await this.#answered(bookId, id, gone.kind === 'refused' && gone.error === 'not-found' ? { kind: 'ok' } : gone);
        if (outcome !== 'ok') return { pushed, problem: outcome === 'stop' ? (problem ?? 'failed') : outcome };
        await store.markShared(note.id, id, { epoch: shared.epoch, sent: 'deleted' });
        continue;
      }
      if (note.origin !== 'writer' || shared.sent === 'refused') continue;
      const first = note.parentId === null ? note : byId.get(note.parentId);
      // RO: a reply waits for its standing first note to be at the relay.
      if (first !== note && (first?.deletedAt !== null || !(heldAtRelay(first.shared?.sent ?? null) || sentNow.has(first.id)))) continue;
      // Table MP (MP3): a note the relay holds as it stands at the range it went with is unchanged: a passage that only
      // moved reaches readers in their manifests, never as the note again.
      if (shared.sent !== null && heldAtRelay(shared.sent)) {
        // Deep-fix r6: what decides its item, so an unchanged note is passed over without building it again.
        const what = JSON.stringify([note.parentId === null, note.chapterId, note.anchor === null, note.title, note.group, note.text, shared.range ?? null, note === first ? null : first.shared]);
        if (this.#matched.get(note.id) === `${shared.sent}\u0000${what}`) continue;
        const asSent = itemOfNote(note, first, shared.range ?? null);
        if (asSent.kind === 'item' && shared.sent === (await hashOf(this.#deps.subtle, asSent.plain))) {
          this.#matched.set(note.id, `${shared.sent}\u0000${what}`);
          continue;
        }
      }
      // Table AR: a changed note goes with its passage as found now.
      const located = this.#located(note, book);
      const item = itemOfNote(note, first, located);
      if (item.kind === 'too-large') problem = 'note-too-large';
      if (item.kind !== 'item') continue;
      // (A note held as it stands was passed over above: every send marks the range it went with, MP3.)
      const hash = await hashOf(this.#deps.subtle, item.plain);
      const sent = await this.#send(bookId, id, secrets, list, { note, shared, group }, item.plain, hash, relay, located);
      if (sent === 'stored') {
        pushed += 1;
        sentNow.add(note.id);
      }
      // WN11: a note too large sealed is left, the rest go.
      else if (sent === 'note-too-large') problem = sent;
      else if (sent === 'notes-full' || sent === 'ended') return { pushed, problem: sent };
      else if (sent === 'stop') return { pushed, problem: problem ?? 'failed' };
    }
    return { pushed, problem };
  }

  /**
   * Deep-fix r3, table AR: where the writer's anchored first note `note` is found now in its chapter's text in `book`
   * (null: its passage is gone, or its chapter; it goes as the whole chapter). A chapter is parsed again only when its
   * text changed.
   */
  #located(note: NoteRecord, book: CopyBook): TextRange | null {
    // Deep-fix r6: the book's chapters by ID, made once a book.
    let byId = this.#chapters.get(book);
    if (byId === undefined) {
      byId = new Map(book.chapters.map((each) => [each.id, each]));
      this.#chapters.set(book, byId);
    }
    if (note.anchor === null || note.parentId !== null) return null;
    // (A note on a passage is on a chapter: NR rules.)
    const chapter = byId.get(String(note.chapterId));
    if (chapter === undefined) return null;
    // Deep-fix r4: a note pulled from another writer device holds no quote (AQ) to find it by: its range as it came.
    if (note.anchor.quote === '') return note.anchor.range;
    let held = this.#places.get(chapter.id);
    if (held?.text !== chapter.text) {
      held = { text: chapter.text, places: ChapterPlaces.of(chapter.text), found: new Map() };
      this.#places.set(chapter.id, held);
    }
    const { range, quote, before, after } = note.anchor;
    const key = JSON.stringify([range?.from ?? null, range?.to ?? null, quote, before, after]);
    const known = held.found.get(key);
    if (known !== undefined) return known;
    const found = held.places.locate(note.anchor, new ScanBudget());
    held.found.set(key, found);
    return found;
  }

  /** An owner answer read: `ok`; the record ended (DN12); or `stop` (no answer, rate-limited, refused otherwise). */
  async #answered(bookId: string, id: string, reply: { readonly kind: 'ok' } | { readonly kind: 'refused'; readonly error: RelayError } | { readonly kind: 'failed' }): Promise<'ok' | 'ended' | 'stop'> {
    if (reply.kind === 'ok') return 'ok';
    if (reply.kind === 'failed') return 'stop';
    const end = endOf(reply.error);
    if (end === null) return 'stop';
    await this.#deps.turn(bookId, async () => {
      const record = await this.#deps.store.share(id);
      if (record !== undefined) await this.#deps.end(record, end);
    });
    return 'ended';
  }

  /** WN2, WN3, WN6–WN11: one writer's note sealed under its key, tagged (DN7) and posted; `sent` marked on the relay's word. */
  async #send(
    bookId: string,
    id: string,
    secrets: ShareSecrets,
    list: OwnerList,
    { note, shared, group }: SharedNote,
    plain: Uint8Array<ArrayBuffer>,
    hash: string,
    relay: RelayClient,
    range: TextRange | null,
  ): Promise<'stored' | 'skipped' | 'stop' | 'ended' | 'notes-full' | 'note-too-large'> {
    const { subtle, random, now, store } = this.#deps;
    const record = await store.share(id);
    if (record?.ended !== null) return 'ended';
    let key: Uint8Array<ArrayBuffer>;
    let epoch: number;
    if (group === 'person') {
      // WN6: the invite's person key, while its invite is open (by the list, and fix-next 85, CS⁗: by both ends); else
      // the reply stays here.
      const invite = record.invites.find((each) => each.id === shared.thread);
      const listed = invite === undefined ? undefined : list.invites.find((each) => each.id === toBase64Id(invite.id));
      if (invite === undefined || listed === undefined || !inviteOpen({ ...invite, endsAt: earlierEnd(invite.endsAt, listed.endsAt) }, listed.state, now())) return 'skipped';
      key = bytesOfHex(invite.key);
      epoch = 0;
    } else {
      // WN2: a note never sent goes under its group's latest epoch; WN3: one sent stays at its own (the relay keeps it).
      // Deep-fix r3 (table RF): one whose post was tried (its epoch written first) goes again at that epoch, so a post
      // whose answer was lost is the same note to the relay, whatever rotated since.
      const keys = keysOf(record, group);
      const tried = shared.epoch > 0 ? keys.find((each) => each.epoch === shared.epoch) : undefined;
      const held = shared.sent === null ? (tried ?? keys.at(-1)) : keys.find((each) => each.epoch === shared.epoch);
      if (held === undefined) {
        // RK15: an edit of a note under an epoch dropped can't go at its own: never sent again. (A note never sent always
        // has its group's key by now: RK1 made it in this run.)
        await store.markShared(note.id, id, { epoch: shared.epoch, sent: 'refused' });
        return 'skipped';
      }
      key = bytesOfHex(held.key);
      epoch = held.epoch;
    }
    // RF: the epoch it goes under, written before the post (record first).
    if (shared.sent === null && shared.epoch !== epoch) await store.markShared(note.id, id, { epoch, sent: null });
    const place: NotePlace = { share: secrets.id, note: bytesOfHex(shared.note), group, thread: shared.thread === null ? null : bytesOfHex(shared.thread), epoch, author: null };
    const sealed = await sealNote(subtle, key, place, plain, random(IV_BYTES));
    const tag = await writerTag(subtle, bytesOfHex(record.secret), place, sealed);
    const body = new Uint8Array(sealed.length + tag.length);
    body.set(sealed);
    body.set(tag, sealed.length);
    if (body.length > MAX_NOTE_BYTES) return 'note-too-large';
    const answer = await relay.post('/v1/shares/note', writerNoteBody(secrets.share, secrets.proof, place.note, group, epoch, body, place.thread), readNoteSeq);
    if (answer.kind === 'refused') {
      if (answer.error === 'item-exists') {
        // WN8: another note holds the ID at the relay: never sent again.
        await store.markShared(note.id, id, { epoch, sent: 'refused' });
        return 'skipped';
      }
      // WN9: the writer's notes on the share are at the relay's cap.
      if (answer.error === 'share-full') return 'notes-full';
      // WN6: the thread's invite ended meanwhile; the next run leaves it here.
      if (answer.error === 'invite-ended' || answer.error === 'invite-gone') return 'skipped';
    }
    const outcome = await this.#answered(bookId, id, answer.kind === 'ok' ? { kind: 'ok' } : answer);
    if (outcome !== 'ok') return outcome;
    // WN12: marked with what was sent (MP3: and the range it went with); an edit since differs, and goes at the next run.
    await store.markShared(note.id, id, { epoch, sent: hash, range });
    return 'stored';
  }

  /** Table PN: the share's notes pulled page after page, each opened and stored, then the page's acknowledged. How many were stored. */
  async #pull(bookId: string, id: string, secrets: ShareSecrets, book: CopyBook, relay: RelayClient): Promise<number> {
    const { store } = this.#deps;
    // Table DA: the relay keeps this device's marks apart from the writer's other devices'; no key, no pull.
    const device = await this.#label(secrets);
    if (device === null) return 0;
    let after = 0;
    let stored = 0;
    // PN4: the notes this pull makes, so a reply finds a first note pulled before it, on any page.
    const made = new Map<string, NoteRecord>();
    // Deep-fix r3, table RP: replies whose first note isn't here yet (an edit moved it after them), placed at the pull's end.
    const deferred: PulledNote[] = [];
    for (;;) {
      const page = await relay.post('/v1/shares/pull', shareBody(secrets.share, secrets.proof, { after, device }), readPulledNotes);
      const record = await store.share(id);
      if (record === undefined) return stored;
      // EP: the relay holds the share no more (swept): never pulled again.
      if (page.kind === 'refused' && page.error === 'share-gone') {
        await this.#deps.turn(bookId, () => this.#deps.end(record, 'gone'));
        return stored;
      }
      if (page.kind !== 'ok') return stored;
      const done = await this.#store(bookId, record, secrets, book, page.value.notes, made, deferred, relay, device);
      if (done === null) return stored;
      stored += done;
      const last = page.value.notes.at(-1);
      if (!page.value.more || last === undefined) break;
      after = last.seq;
    }
    const record = await store.share(id);
    if (record === undefined || deferred.length === 0) return stored;
    // RP: what is still missing its first note goes to the Notebook (PN5).
    return stored + ((await this.#store(bookId, record, secrets, book, deferred, made, null, relay, device)) ?? 0);
  }

  /**
   * Table PN: `notes` opened, stored and acknowledged (DN9: once stored or found here, PN6; one that doesn't open never);
   * a reply whose first note isn't here is put in `deferred` (RP) when given one, unacknowledged. How many were stored, or
   * null when the store didn't take them.
   */
  async #store(
    bookId: string,
    record: ShareRecord,
    secrets: ShareSecrets,
    book: CopyBook,
    notes: readonly PulledNote[],
    made: Map<string, NoteRecord>,
    deferred: PulledNote[] | null,
    relay: RelayClient,
    device: string,
  ): Promise<number | null> {
    const changes: PulledChange[] = [];
    const acks: { readonly id: string; readonly seq: number }[] = [];
    for (const note of notes) {
      const change = await this.#changeOf(bookId, record, secrets, book, note, made, deferred !== null);
      if (change === null) continue;
      if (change === 'defer') {
        deferred?.push(note);
        continue;
      }
      if (change !== 'held') changes.push(change);
      if (typeof change === 'object' && change.kind === 'create') made.set(change.note.id, change.note);
      acks.push({ id: toBase64Url(note.id), seq: note.seq });
    }
    const applied = changes.length === 0 ? null : await this.#deps.store.applyPulled(changes);
    if (applied !== null && applied.kind !== 'applied') return null;
    for (let at = 0; at < acks.length; at += MAX_NOTE_ACKS) {
      // PN16: a lost acknowledgement leaves them for the next pull, which finds them stored.
      await relay.post('/v1/shares/ack', shareBody(secrets.share, secrets.proof, { notes: acks.slice(at, at + MAX_NOTE_ACKS), device }), readNotesAcknowledged);
    }
    return applied?.results.filter((result) => result === 'stored').length ?? 0;
  }

  /** The key a pulled note at its place opens with (DN6): its thread's person key, or its group's key at its epoch; null for none here. */
  #keyOf(record: ShareRecord, note: PulledNote): Uint8Array<ArrayBuffer> | null {
    if (note.group === 'person') {
      const thread = note.thread === null ? null : toHex(note.thread);
      // PN14: a person's note is in its author's own thread (or the writer's, in any thread).
      if (thread === null || note.epoch !== 0 || (note.author !== null && toHex(note.author) !== thread)) return null;
      const invite = record.invites.find((each) => each.id === thread);
      return invite === undefined ? null : bytesOfHex(invite.key);
    }
    if (note.thread !== null) return null;
    const held = record.roleKeys.find((key) => key.group === note.group && key.epoch === note.epoch);
    return held === undefined ? null : bytesOfHex(held.key);
  }

  /**
   * Table PN: the change pulled `note` brings; `held` when it is here already and changes nothing (PN6); `defer` for a
   * reply whose first note isn't here yet, when `defer` (RP); null when it is no note to store (PN8, PN10, PN11, PN14).
   */
  async #changeOf(bookId: string, record: ShareRecord, secrets: ShareSecrets, book: CopyBook, note: PulledNote, made: ReadonlyMap<string, NoteRecord>, defer: boolean): Promise<PulledChange | 'held' | 'defer' | null> {
    const { subtle, store, now } = this.#deps;
    const writer = note.author === null;
    // DN8: a writer device's note keeps its own ID; an invitee's is derived from the share and its relay ID.
    const local = writer ? toHex(note.id) : await this.#localId(secrets, note.id);
    // PN9, deep-fix r1 (table TD′): a tombstone deletes only an invitee's note held across this share under this relay ID;
    // a writer-authored one is never taken on the relay's word (nothing authenticates it), only acknowledged.
    if (note.body === null) return writer ? 'held' : { kind: 'delete', id: local, share: record.id, note: toHex(note.id) };
    const key = this.#keyOf(record, note);
    if (key === null) return null;
    const place: NotePlace = { share: secrets.id, note: note.id, group: note.group, thread: note.thread, epoch: note.epoch, author: note.author };
    let sealed: Uint8Array<ArrayBuffer> = note.body;
    if (writer) {
      // DN7, PN8: a writer's note is its sealed bytes and the writer's tag, which must hold.
      if (note.body.length <= WRITER_TAG_BYTES) return null;
      sealed = note.body.slice(0, -WRITER_TAG_BYTES);
      if (!(await writerTagHolds(subtle, bytesOfHex(record.secret), place, sealed, note.body.slice(-WRITER_TAG_BYTES)))) return null;
    }
    const plain = await openNote(subtle, key, place, sealed);
    const item: ShareNote | null = plain === null ? null : readShareNote(plain);
    if (plain === null || item === null) return null;
    const held = await store.note(local);
    const hash = await hashOf(subtle, plain);
    // PN6: a writer's note held here is this device's (or taken before): acknowledged, unchanged. PN12: an invitee's edit.
    if (held !== undefined) return writer ? 'held' : { kind: 'update', id: local, text: item.text, sent: hash };
    const authorId = note.author === null ? null : toHex(note.author);
    const author = record.invites.find((invite) => invite.id === authorId);
    const thread = note.thread === null ? null : toHex(note.thread);
    const first = item.kind === 'reply' ? await this.#firstOf(secrets, record.id, item.to, made) : undefined;
    // RP: a reply whose first note isn't here yet waits for the rest of the pull.
    if (item.kind === 'reply' && first === undefined && defer) return 'defer';
    const shared: NoteShare = { share: record.id, note: toHex(note.id), thread, author: authorId, epoch: note.epoch, sent: hash };
    const pulled = noteOfPulled(item, { id: local, shared, visibility: note.group, bookId, author, first, book, writtenAt: note.at, now: now() });
    if (!writer) return { kind: 'create', note: pulled.note };
    // PN7: another writer device's note, marked with what this device would send of it, so it never goes out again from here.
    const placed = pulled.item.kind === 'note' ? (pulled.item.anchor?.range ?? null) : null;
    return { kind: 'create', note: { ...pulled.note, shared: { ...shared, sent: await hashOf(subtle, encodeShareNote(pulled.item)), ...(placed === null ? {} : { range: placed }) } } };
  }

  /** DN8: an invitee's note's local ID: the first 16 bytes of SHA-256 of its domain, the share and its relay ID. */
  async #localId(secrets: ShareSecrets, note: Uint8Array): Promise<string> {
    return toHex(await this.#named('cockatrice/invite/note-id/v1', secrets, note));
  }

  /**
   * Table DA, DA1: this device's label on the share, base64url: the first 16 bytes of SHA-256 of its domain, the share
   * and the device's public key (one share's label tells nothing of another's); null with no key.
   */
  async #label(secrets: ShareSecrets): Promise<string | null> {
    const key = await this.#deps.device();
    return key === null ? null : toBase64Url(await this.#named('cockatrice/invite/device/v1', secrets, key));
  }

  /** The first 16 bytes of SHA-256 of `domain`, the share's ID and `tail`. */
  async #named(domain: string, secrets: ShareSecrets, tail: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
    const head = new TextEncoder().encode(domain);
    const bytes = new Uint8Array(head.length + secrets.id.length + tail.length);
    bytes.set(head);
    bytes.set(secrets.id, head.length);
    bytes.set(tail, head.length + secrets.id.length);
    return new Uint8Array(await this.#deps.subtle.digest('SHA-256', bytes)).slice(0, 16);
  }

  /**
   * PN4: the first note of the thread a reply names (by its relay ID `to`), when held here or made earlier in this page
   * (`made`): the note, or the first note it answers.
   */
  async #firstOf(secrets: ShareSecrets, share: string, to: string, made: ReadonlyMap<string, NoteRecord>): Promise<NoteRecord | undefined> {
    const read = async (id: string): Promise<NoteRecord | undefined> => made.get(id) ?? (await this.#deps.store.note(id));
    for (const candidate of [to, await this.#localId(secrets, bytesOfHex(to))]) {
      const named = await read(candidate);
      if (named?.shared?.share !== share || named.shared.note !== to) continue;
      return named.parentId === null ? named : read(named.parentId);
    }
    return undefined;
  }
}
